// src/hooks/usePreferences.js
// Personal preferences from Settings, saved to the signed-in person's
// account (portal /me) so they follow them to any browser. A per-person
// copy in this browser makes them available instantly on the next visit
// and keeps them working if the server can't be reached.
//
// First sign-in after this change: settings already chosen in this browser
// are carried over to the account if the account has none yet.
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { fetchMe, updateMe } from '../utils/api';
import { DEFAULT_TIME_ZONE, setTimeZone } from '../utils/timeZone';

const LEGACY_KEY = 'faxPortal.preferences';            // before settings were per account
const keyFor     = email => `faxPortal.preferences:${String(email || '').toLowerCase()}`;
const SAVE_DELAY_MS = 600;

export const DEFAULT_PREFERENCES = {
  listSize  : '50',       // faxes shown by default on Outgoing / Incoming
  newFaxes  : 'auto',     // 'auto' = add new faxes when idle, 'bar' = always wait behind "Show"
  faxNotices: true,       // pop-up notices for fax updates (delivered, failed, received…)
  startTab  : 'dashboard', // tab to open on sign-in: 'dashboard' | 'compose' | 'outbox' | 'incoming' (utils/tabRoutes START_TABS; old 'outgoing' = Overview)
  timeZone  : DEFAULT_TIME_ZONE, // times in the portal (My profile)
  mutedClients: [],        // Super Admins: clients whose fax pop-ups they don't want (3.24.0)
  // Which kinds of fax pop-up they want (3.30.1), among those their role
  // allows (Roles & Access → Fax pop-ups). Incoming faxes that failed to
  // arrive are off unless ticked.
  popupKinds: { delivered: true, failed: true, received: true, incomingFailed: false },
  // The Overview's own arrangement (3.34.1): { cards: [ids], panels: [ids] }; null = the standard order
  overviewLayout: null,
  showTraining: true,      // the Library's training and Mark as done (3.35.0)
  appearance: '',          // 'auto' (same as the computer) | 'light' | 'dark' (3.45.0); '' = their client's starting mode, else Auto (3.47.0)
  shortcuts: true,         // Gmail-style single-key shortcuts (3.47.0, hooks/useShortcuts)
  theme: '',              // 'teal' | 'ocean' | 'plum'; '' = their client's starting theme (3.46.0, theme/themes)
  tourDone: 0,             // the guided tour's version they finished or skipped (3.53.0, components/GuidedTour)
};

function readLocal(key) {
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) || 'null');
    return raw && typeof raw === 'object' ? raw : null;
  } catch { return null; }
}
function writeLocal(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const { user, getToken } = useAuth();
  const email = user?.email || '';
  const [prefs, setPrefs]         = useState(DEFAULT_PREFERENCES);
  const [loadedFor, setLoadedFor] = useState('');   // email whose account settings are loaded
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimer = useRef(null);
  const pending   = useRef({});
  const tokenRef  = useRef(getToken);
  tokenRef.current = getToken;

  // On sign-in: show the browser copy at once, then load the account's settings
  useEffect(() => {
    clearTimeout(saveTimer.current);
    pending.current = {};
    setLoadedFor('');
    if (!email) { setPrefs(DEFAULT_PREFERENCES); return; }
    const local = readLocal(keyFor(email)) || readLocal(LEGACY_KEY) || {};
    setPrefs({ ...DEFAULT_PREFERENCES, ...local });
    let cancelled = false;
    fetchMe(() => tokenRef.current())
      .then(async me => {
        if (cancelled) return;
        const server = me.preferences || {};
        let merged;
        if (Object.keys(server).length) {
          merged = { ...DEFAULT_PREFERENCES, ...server };
        } else {
          merged = { ...DEFAULT_PREFERENCES, ...local };
          // Carry this browser's choices over to the account
          if (Object.keys(local).length) {
            await updateMe({ preferences: merged }, () => tokenRef.current()).catch(() => {});
          }
        }
        setPrefs(merged);
        writeLocal(keyFor(email), merged);
        setLoadedFor(email);
      })
      .catch(() => { if (!cancelled) setLoadedFor(email); });  // keep the browser copy
    return () => { cancelled = true; };
  }, [email]);

  const setPreference = useCallback((key, value) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: value };
      if (email) writeLocal(keyFor(email), next);
      return next;
    });
    if (!email) return;
    // Save to the account shortly after the last change
    pending.current = { ...pending.current, [key]: value };
    setSaveState('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const changes = pending.current;
      pending.current = {};
      try {
        await updateMe({ preferences: changes }, () => tokenRef.current());
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, SAVE_DELAY_MS);
  }, [email]);

  // Every time in the portal follows the chosen zone (set before the tabs render)
  setTimeZone(prefs.timeZone);

  const value = useMemo(() => ({ prefs, setPreference, loadedFor, saveState }), [prefs, setPreference, loadedFor, saveState]);
  return createElement(PreferencesContext.Provider, { value }, children);
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside PreferencesProvider');
  return ctx;
}
